import React, { useEffect, useRef } from 'react';

/** Only one feed video plays at a time (LinkedIn/IG-style). */
let activeVideoEl = null;

function claimPlayback(el) {
  if (activeVideoEl && activeVideoEl !== el && !activeVideoEl.paused) {
    activeVideoEl.pause();
  }
  activeVideoEl = el;
}

function releasePlayback(el) {
  if (activeVideoEl === el) activeVideoEl = null;
}

/**
 * Feed video that autoplays (muted) when mostly on screen and pauses when scrolled away.
 *
 * - Starts muted + playsInline for autoplay policy.
 * - Does NOT loop: looping WebM/Opus on mobile often crackles at the seam when unmuted.
 * - Uses enter/leave hysteresis so brief scroll jitter does not pause/play-thrash audio.
 * - If the user unmutes via controls, we stop forcing muted and avoid aggressive replay fights.
 */
export default function FeedPostVideo({
  src,
  'data-testid': dataTestId = 'post-video',
  style,
  compact = false,
}) {
  const videoRef = useRef(null);
  const unmutedByUserRef = useRef(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;

    unmutedByUserRef.current = false;
    // HTML muted attribute is what Chrome checks for autoplay permission.
    video.muted = true;
    video.defaultMuted = true;

    const onVolumeChange = () => {
      // Native controls unmute — treat as intentional listening.
      if (!video.muted) {
        unmutedByUserRef.current = true;
      } else {
        unmutedByUserRef.current = false;
      }
    };
    video.addEventListener('volumechange', onVolumeChange);

    if (typeof IntersectionObserver === 'undefined') {
      return () => {
        video.removeEventListener('volumechange', onVolumeChange);
      };
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!video) return;
        const ratio = entry.intersectionRatio || 0;
        const mostlyVisible = entry.isIntersecting && ratio >= 0.55;
        // Leave threshold lower than enter to avoid pause/play thrash while scrolling.
        const clearlyAway = !entry.isIntersecting || ratio < 0.25;

        if (mostlyVisible) {
          claimPlayback(video);
          // Do not re-mute if the user already unmuted.
          if (!unmutedByUserRef.current) {
            video.muted = true;
          }
          const playPromise = video.play();
          if (playPromise?.catch) playPromise.catch(() => {});
        } else if (clearlyAway && !video.paused) {
          video.pause();
          releasePlayback(video);
        }
      },
      { threshold: [0, 0.25, 0.55, 0.75, 1] }
    );

    observer.observe(video);

    return () => {
      observer.disconnect();
      video.removeEventListener('volumechange', onVolumeChange);
      video.pause();
      releasePlayback(video);
    };
  }, [src]);

  return (
    <video
      ref={videoRef}
      src={src}
      controls
      muted
      playsInline
      preload="metadata"
      data-testid={dataTestId || (compact ? 'post-video-constrained' : 'post-video')}
      style={style}
    />
  );
}
